import './styles/_recommended-products.scss';
import Swiper, { Mousewheel } from 'swiper';
import {
  fetchRebuyRecommendedProducts,
  fetchProductsById,
  restructureRestProducts,
  fetchProductRecommendations,
} from '../services/products';
import 'swiper/swiper-bundle.css';

import {
  getProductItemBody,
  attachProductItemEvents,
  renderVideos,
} from '../common/product-item';
import { createLazyAction } from '../utils/lazy-action';
import { renderPlaceholderContainers } from '../common/placeholder-loader';
import { waitObjectProperty } from '../utils/performance';
import { onDocumentReady } from '../utils/dom';

Swiper.use([Mousewheel]);

const sliderOptions = {
  slidesPerView: 2.15,
  freeMode: false,
  loop: true,
  spaceBetween: 5,
  mousewheel: {
    forceToAxis: true,
    sensitivity: 1,
    releaseOnEdges: true,
    thresholdDelta: 10,
    thresholdTime: 200,
  },
  breakpoints: {
    851: {
      slidesPerView: 4.15,
      freeMode: false,
      loop: true,
    },
  },
};

const state = {
  recommendedProducts: [],
  renderedRecommendedItems: {},
  renderedStyledWithItems: {},
  renderedRecentItems: {},
  settings: {},
  elements: {},
  recommendationsSlider: null,
  viewedProducts: [],
  viewedProductsSlider: null,
  styledWithSlider: null,
  viewedProductIds: [],
  currentProductId: null,
};

const enableAutoScrolling = (sliderElement, sliderInstance) => {
  let autoScrollInterval = null;
  const SCROLL_THRESHOLD = 50;
  const SCROLL_INTERVAL = 150;
  const TRANSITION_SPEED = 1500;

  const startAutoScroll = (direction) => {
    stopAutoScroll();
    autoScrollInterval = setInterval(() => {
      if (direction === "next") {
        sliderInstance.slideNext(TRANSITION_SPEED);
        if (sliderInstance.isEnd) sliderInstance.slideTo(0, TRANSITION_SPEED);
      } else if (direction === "prev") {
        sliderInstance.slidePrev(TRANSITION_SPEED);
        if (sliderInstance.isBeginning) {
          sliderInstance.slideTo(sliderInstance.slides.length - 1, TRANSITION_SPEED);
        }
      }
    }, SCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    if (autoScrollInterval) clearInterval(autoScrollInterval);
  };

  sliderElement.addEventListener("mousemove", ({ clientX }) => {
    const { left, width } = sliderElement.getBoundingClientRect();
    const x = clientX - left;

    stopAutoScroll();

    if (x > width - SCROLL_THRESHOLD) {
      startAutoScroll("next");
    } else if (x < SCROLL_THRESHOLD) {
      startAutoScroll("prev");
    }
  });

  sliderElement.addEventListener("mouseleave", stopAutoScroll);
};

const init = (showViewedProducts, showRecommendedProducts, showStyledWithProducts) => (
  saveViewedProduct(),
  cacheElements(),
  renderPlaceholderContainers(state.elements.viewedProductsLoader, 4, 'product'),
  renderPlaceholderContainers(state.elements.recommendationsLoader, 4, 'product'),
  renderPlaceholderContainers(state.elements.styledWithProductsLoader, 4, 'product'),
  showViewedProducts && applyViewedProductsList()(),
  hideViewedProducts(),
  showRecommendedProducts && applyRecommendedProductsList()(),
  showStyledWithProducts && applyStyledWithProductsList()()
);

const cacheElements = () => {
  state.settings = window.recommendedProductsSettings || {};
  state.settings.styledWithProducts = window?.productMetafields?.styledWith;
  const recommendationsSection = document.querySelector('.recommendations--main');
  const viewedProductsSection = document.querySelector('.recommendations--recent');
  const styledWithProductsSection = document.querySelector('.recommendations--styled');
  state.elements = {
    recommendationsSection,
    viewedProductsSection,
    styledWithProductsSection,
    recommendationsLoader: recommendationsSection?.querySelector('.recommendations__loader'),
    viewedProductsLoader: viewedProductsSection?.querySelector('.recommendations__loader'),
    styledWithProductsLoader: styledWithProductsSection?.querySelector('.recommendations__loader')
  };
};

const hideViewedProducts = (forceHide) => {
  if (!window.viewedProductsSettings) return;
  if (state.viewedProductIds.length && !forceHide) return;
  state.elements.viewedProductsSection.style.display = 'none';
};

const applyRecommendedProductsList = () => (
  createLazyAction(
    state.elements.recommendationsSection,
    async () => (
      await getRecommendedProducts(),
      initRecommendationsSlider(),
      await renderProducts(
        state.recommendedProducts,
        state.renderedRecommendedItems,
        state.elements.recommendationsSection,
        state.recommendationsSlider,
        true,
      )
    ),
    '.recommendations__loader',
  )
);

const applyStyledWithProductsList = () => (
  createLazyAction(
    state.elements.styledWithProductsSection,
    async () => (
      getStyledWithProducts(),
      initStyledWithSlider(),
      await renderProducts(
        state.styledWithProducts,
        state.renderedStyledWithItems,
        state.elements.styledWithProductsSection,
        state.styledWithSlider,
        false
      )
    ),
    '.recommendations__loader',  
  )
);

const getViewedProducts = async () => {
  if (!window.viewedProductsSettings) return;
  try {
    const savedProductIds = state.viewedProductIds.filter(({ id }) => (
      +id !== +state.currentProductId
    ));
    if (!savedProductIds.length) {
      hideViewedProducts(true);
      return;
    }
    state.viewedProducts = await fetchProductsById(savedProductIds);
    hideViewedProducts(!state.viewedProducts.length);
  } catch (er) {
    console.log(er);
    hideViewedProducts(true);
  }
};

const saveViewedProduct = () => {
  if (!window.viewedProductsSettings) return;
  state.viewedProductIds = JSON.parse(localStorage.getItem('viewed-products') || '[]');

  if (document.body.dataset.templateName !== 'product') return;

  state.currentProductId = document.querySelector('.Product').dataset.productId;

  const isCurrentProductAlreadyViewed = state.viewedProductIds.findIndex(({ id }) => (
    id === state.currentProductId?.toString()
  )) > -1;

  if (isCurrentProductAlreadyViewed) return;

  const newProductIdsList = [...state.viewedProductIds];
  const { max_products_saved } = window.viewedProductsSettings;

  if (state.viewedProductIds.length === max_products_saved) {
    newProductIdsList.pop();
  }

  newProductIdsList.unshift({ id: state.currentProductId });

  localStorage.setItem('viewed-products', JSON.stringify(newProductIdsList));
};

const applyViewedProductsList = () => (
  createLazyAction(
    state.elements.viewedProductsSection,
    async () => (
      await getViewedProducts(),
      initViewedProductsSlider(),
      await renderProducts(
        state.viewedProducts,
        state.renderedRecentItems,
        state.elements.viewedProductsSection,
        state.viewedProductsSlider,
      )
    ),
    '.recommendations__loader',
  )
);

const initRecommendationsSlider = () => {
  const sliderElement = document.querySelector('.recommendations--main .recommendations__slider');
  state.recommendationsSlider = new Swiper(sliderElement, {
    ...sliderOptions,
  });

  // enableAutoScrolling(sliderElement, state.recommendationsSlider);
};

const initStyledWithSlider = () => {
  const sliderElement = document.querySelector('.recommendations--styled .recommendations__slider');
  state.styledWithSlider = new Swiper(sliderElement, {
    ...sliderOptions,
  });

  // enableAutoScrolling(sliderElement, state.styledWithSlider);
};

const initViewedProductsSlider = () => {
  const sliderElement = document.querySelector('.recommendations--recent .recommendations__slider');
  state.viewedProductsSlider = new Swiper(sliderElement, {
    ...sliderOptions,
  });

  // enableAutoScrolling(sliderElement, state.viewedProductsSlider);
};

const hideRecommendedProducts = () => {
  state.elements.recommendationsSection.classList.add('hidden');
};

const getRecommendedProducts = async () => {
  try {
    const { max_products_recommended, api_type } = window.recommendedProductsSettings;
    if (api_type === 'rebuy') {
      state.recommendedProducts = await fetchRebuyRecommendedProducts(
        [window.products[0].id],
        max_products_recommended,
      );
    } else {
      state.recommendedProducts =
          await fetchProductRecommendations(window.products[0].id, max_products_recommended);
    }
    if (!state.recommendedProducts.length) hideRecommendedProducts();
  } catch (er) {
    console.log(er);
    hideRecommendedProducts();
  }
};

const getStyledWithProducts = () => {
  if (state.settings.styledWithProducts) {
    state.styledWithProducts = restructureRestProducts(state.settings.styledWithProducts);
  } else {
    state.styledWithProducts = null;
    state.elements.styledWithProductsSection.classList.add('hidden');
  }
};

const renderProducts = async (
  products,
  renderedState,
  itemsContainer,
  slider,
  isRecommendedProdsSection,
) => {
  if (!products?.length) return;
  await waitObjectProperty("GLBE_PARAMS", window);
  if (GLBE_PARAMS.currencyCode !== "USD") {
    await waitObjectProperty("PriceInfo", window.GLBE_PARAMS);
  }
  const productTemplates = products.map((product, index) => {
    const newProduct = {...product};
    if (GLBE_PARAMS.currencyCode !== "USD") {
      if (newProduct.compareAtPrice) {
        newProduct.compareAtPrice = glbeApp.modules.priceInfo.convertPrice(newProduct.compareAtPrice);
      }
      newProduct.price = glbeApp.modules.priceInfo.convertPrice(product.price);
    }
    const template = getProductItemBody(
      newProduct,
      index,
      renderedState,
      false,
      itemsContainer,
    );
    return template.replace('"product-item ', '"product-item swiper-slide ');
  });
  slider.appendSlide(productTemplates);
  attachProductItemEvents(
    renderedState,
    itemsContainer,
    isRecommendedProdsSection,
  );
  await renderVideos(products, renderedState);
  slider.update();
};

onDocumentReady(() => init(false, true, false));
